import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notificationService } from '../../services/notificationService';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NotificationFeed from './components/NotificationFeed';
import MessageTemplateBuilder from './components/MessageTemplateBuilder';
import NotificationSettings from './components/NotificationSettings';
import DeliveryMonitor from './components/DeliveryMonitor';
import TemplateManager from './components/TemplateManager';

const RealTimeNotificationCommunicationHub = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('notifications');
  const [unreadCount, setUnreadCount] = useState(0);

  const subscriptionRef = useRef(null);

  const breadcrumbItems = useMemo(() => ([
    { label: 'Inicio', path: '/', icon: 'Home' },
    { label: isAdmin ? 'Admin Dashboard' : 'Dashboard', path: isAdmin ? '/admin-dashboard' : '/user-dashboard', icon: 'LayoutDashboard' },
    { label: 'Notification Hub', path: '/real-time-notification-communication-hub', icon: 'Bell' }
  ]), [isAdmin]);

  const computeUnread = (list) => (list || [])?.filter(n => !n?.read)?.length;

  const upsertById = (list, item) => {
    if (!item?.id) return list || [];
    const prev = list || [];
    const idx = prev?.findIndex(x => x?.id === item?.id);
    if (idx === -1) return [item, ...prev];
    const next = [...prev];
    next[idx] = item;
    return next;
  };

  const loadNotifications = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const data = await notificationService?.getNotifications(user?.id, { limit: 50 });
      const list = Array.isArray(data) ? data : [];
      setNotifications(list);
      setUnreadCount(computeUnread(list));
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const templatesData = await notificationService?.getMessageTemplates({ activeOnly: true });
      setTemplates(Array.isArray(templatesData) ? templatesData : []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const subscribeToNotifications = () => {
    if (!user?.id) return null;

    // Evita doble suscripción
    if (subscriptionRef?.current) {
      //notificationService.unsubscribeFromNotifications(subscriptionRef.current);
      subscriptionRef.current = null;
    }

    const sub = notificationService?.subscribeToNotifications(user?.id, (payload) => {
      try {
        if (!payload) return;

        const eventType = payload?.eventType;
        const rowNew = payload?.new;
        const rowOld = payload?.old;

        if (eventType === 'INSERT' && rowNew?.id) {
          setNotifications(prev => {
            const next = upsertById(prev, rowNew);
            setUnreadCount(computeUnread(next));
            return next;
          });
          return;
        }

        if (eventType === 'UPDATE' && rowNew?.id) {
          setNotifications(prev => {
            const next = upsertById(prev, rowNew);
            setUnreadCount(computeUnread(next));
            return next;
          });
          return;
        }

        if (eventType === 'DELETE') {
          const id = rowOld?.id;
          if (!id) return;
          setNotifications(prev => {
            const next = (prev || [])?.filter(n => n?.id !== id);
            setUnreadCount(computeUnread(next));
            return next;
          });
        }
      } catch (e) {
        console.error('Realtime payload error:', e);
      }
    });

    subscriptionRef.current = sub;
    return sub;
  };

  useEffect(() => {
    if (!user?.id) return;

    loadNotifications();
    loadTemplates();
    subscribeToNotifications();

    return () => {
  try {
    notificationService?.unsubscribe(); // esto ya remueve el channel interno
  } catch (e) {
    console.error('Unsubscribe error:', e);
  } finally {
    subscriptionRef.current = null;
  }
};

  }, [user?.id, isAdmin]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService?.markAsRead(notificationId);

      setNotifications(prev => {
        const next = (prev || [])?.map(n => (n?.id === notificationId ? { ...n, read: true } : n));
        setUnreadCount(computeUnread(next));
        return next;
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService?.markAllAsRead(user?.id);
      setNotifications(prev => {
        const next = (prev || [])?.map(n => ({ ...n, read: true }));
        setUnreadCount(0);
        return next;
      });
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleTemplateCreate = async (templateData) => {
    try {
      await notificationService?.createMessageTemplate(templateData, user?.id);
      await loadTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando centro de notificaciones...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />

      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 mt-16 sm:mt-20">
        <BreadcrumbTrail items={breadcrumbItems} />

        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-2 flex items-center flex-wrap gap-2">
                <Icon name="Bell" size={28} className="text-primary flex-shrink-0" />
                <span>Real-Time Notification & Communication Hub</span>
              </h1>
              <p className="text-xs sm:text-sm lg:text-base text-muted-foreground">
                Manage notifications, message templates, and communication workflows
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {unreadCount > 0 && (
                <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 text-primary rounded-lg font-semibold text-xs sm:text-sm">
                  {unreadCount} unread
                </div>
              )}
              <Button
                iconName="ArrowLeft"
                variant="outline"
                onClick={() => navigate(isAdmin ? '/admin-dashboard' : '/user-dashboard')}
                className="text-xs sm:text-sm"
              >
                Back
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs - Mobile optimized with horizontal scroll */}
        <div className="flex space-x-2 mb-4 sm:mb-6 border-b border-border overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-3 sm:px-4 py-2 font-medium transition-colors whitespace-nowrap relative text-xs sm:text-sm touch-target ${
              activeTab === 'notifications'
                ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Bell" size={16} className="inline mr-2" />
            Notifications
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('templates')}
            className={`px-3 sm:px-4 py-2 font-medium transition-colors whitespace-nowrap text-xs sm:text-sm touch-target ${
              activeTab === 'templates' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="FileText" size={16} className="inline mr-2" />
            Templates
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className={`px-3 sm:px-4 py-2 font-medium transition-colors whitespace-nowrap text-xs sm:text-sm touch-target ${
              activeTab === 'settings' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Settings" size={16} className="inline mr-2" />
            Settings
          </button>

          <button
            onClick={() => setActiveTab('monitor')}
            className={`px-3 sm:px-4 py-2 font-medium transition-colors whitespace-nowrap text-xs sm:text-sm touch-target ${
              activeTab === 'monitor' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Activity" size={16} className="inline mr-2" />
            Monitor
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {activeTab === 'notifications' && (
              <NotificationFeed
                notifications={notifications}
                onMarkAsRead={handleMarkAsRead}
                onMarkAllAsRead={handleMarkAllAsRead}
              />
            )}

            {activeTab === 'templates' && (
              <TemplateManager
                templates={templates}
                onRefresh={loadTemplates}
                onTemplateCreate={handleTemplateCreate}
              />
            )}

            {activeTab === 'settings' && (
              <NotificationSettings userId={user?.id} />
            )}

            {activeTab === 'monitor' && isAdmin && (
              <DeliveryMonitor />
            )}
          </div>

          <div className="lg:col-span-1">
            {(activeTab === 'notifications' || activeTab === 'templates') && (
              <MessageTemplateBuilder
                templates={templates}
                onTemplateCreate={handleTemplateCreate}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeNotificationCommunicationHub;
